"use client";

import { Edit, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from 'sonner';
import { z } from "zod";
import { trpc } from "~/api/trpc";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "~/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "~/components/ui/dialog";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Textarea } from "~/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "~/components/ui/alert-dialog";
import { useUserStore } from "~/stores/user-store";
import { useShallow } from "zustand/react/shallow";
import { UserRole } from "~/types/server-types/UserRole";

type TreatmentStep = {
    id: string;
    name: string;
    shortDescription: string;
    minuteStart: number;
    minuteEnd: number;
    roomCategoryConstraints?: Array<{ roomCategory: { id: string } }>;
    equipmentCategoryConstraints?: Array<{ equipmentCategory: { id: string } }>;
    practitionerCategoryConstraints?: Array<{ practitionerCategory: { id: string } }>;
};

// Form schema
const treatmentStepSchema = z.object({
    name: z.string().min(0, "Name is required"),
    shortDescription: z.string().min(0, "Description is required"),
    minuteStart: z.coerce.number().min(0, "Start time must be at least 0 minutes"),
    minuteEnd: z.coerce.number().min(0, "End time must be at least 0 minutes"),
    roomCategories: z.array(z.string()).optional(),
    equipmentCategories: z.array(z.string()).optional(),
    practitionerCategories: z.array(z.string()).optional(),
});

type TreatmentStepFormValues = z.infer<typeof treatmentStepSchema>;

type TreatmentStepsProps = {
    variantId: string;
};

const TreatmentSteps = ({ variantId }: TreatmentStepsProps) => {
    const { userRole } = useUserStore(
        useShallow((state) => ({
            userRole: state.user?.role
        }))
    );
    const shouldDisableAllInputs = userRole !== UserRole.ADMIN;

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    
    // TRPC queries and mutations
    const stepsQuery = trpc.treatmentSteps.getAll.useQuery({
        variantId,
        page,
        pageSize,
        filters: {},
    });
    
    // Fetch categories data
    const roomCategoriesQuery = trpc.roomCategories.getAll.useQuery({
        page: 0,
        pageSize: 100,
        filters: {},
    });
    
    const equipmentCategoriesQuery = trpc.equipmentCategories.getAll.useQuery({
        page: 0,
        pageSize: 100,
        filters: {},
    });
    
    const practitionerCategoriesQuery = trpc.practitionerCategories.getAll.useQuery({
        page: 0,
        pageSize: 100,
        filters: {},
    });
    
    const createStepMutation = trpc.treatmentSteps.create.useMutation({
        onSuccess: () => {
            toast.success("Treatment step created successfully");
            setIsAddDialogOpen(false);
            stepsQuery.refetch();
        },
        onError: (error: any) => {
            toast.error(error.message, {
                description: error.message,
            });
        },
    });
    
    const updateStepMutation = trpc.treatmentSteps.update.useMutation({
        onSuccess: () => {
            toast.success("Treatment step updated successfully", {
                description: "Treatment step updated successfully",
            });
            setIsEditDialogOpen(false);
            stepsQuery.refetch();
        },
        onError: (error) => {
            toast.error(error.message, {
                description: error.message,
            });
        },
    });

    const deleteStepMutation = trpc.treatmentSteps.delete.useMutation({
        onSuccess: () => {
            toast.success("Treatment step deleted successfully", {
                description: "The treatment step has been removed from this variant",
            });
            stepsQuery.refetch();
        },
        onError: (error) => {
            toast.error("Failed to delete treatment step", {
                description: error.message,
            });
        },
    });

    const [currentStep, setCurrentStep] = useState<TreatmentStep | null>(null);

    console.log(currentStep);
    
    // Form setup
    const form = useForm<TreatmentStepFormValues>({
        resolver: zodResolver(treatmentStepSchema),
        defaultValues: {
            name: "",
            shortDescription: "",
            minuteStart: 0,
            minuteEnd: 15,
            roomCategories: [],
            equipmentCategories: [],
            practitionerCategories: [],
        },
    });

    const editForm = useForm<TreatmentStepFormValues>({
        resolver: zodResolver(treatmentStepSchema),
        defaultValues: {
            name: "",
            shortDescription: "",
            minuteStart: 0,
            minuteEnd: 15,
            roomCategories: [],
            equipmentCategories: [],
            practitionerCategories: [],
        },
    });

    // Reset form when dialog closes
    useEffect(() => {
        if (!isAddDialogOpen) {
            form.reset();
        }
    }, [isAddDialogOpen, form]);

    // Set form values when editing
    useEffect(() => {
        if (currentStep && isEditDialogOpen) {
            editForm.reset({
                name: currentStep.name,
                shortDescription: currentStep.shortDescription,
                minuteStart: currentStep.minuteStart,
                minuteEnd: currentStep.minuteEnd,
                roomCategories: currentStep.roomCategoryConstraints?.map((c) => c.roomCategory.id.toString()) || [],
                equipmentCategories: currentStep.equipmentCategoryConstraints?.map((c) => c.equipmentCategory.id.toString()) || [],
                practitionerCategories: currentStep.practitionerCategoryConstraints?.map((c) => c.practitionerCategory.id.toString()) || [],
            });
        }
    }, [currentStep, isEditDialogOpen, editForm]);

    const onSubmit = (values: TreatmentStepFormValues) => {
        if(userRole !== UserRole.ADMIN) {
            toast.error("You don't have permission to do this");
            return;
        }
        createStepMutation.mutate({
            variantId,
            name: values.name,
            shortDescription: values.shortDescription,
            minuteStart: values.minuteStart,
            minuteEnd: values.minuteEnd,
            roomCategories: values.roomCategories,
            equipmentCategories: values.equipmentCategories,
            practitionerCategories: values.practitionerCategories,
        });
    };

    const onEditSubmit = (values: TreatmentStepFormValues) => {
        if(userRole !== UserRole.ADMIN) {
            toast.error("You don't have permission to do this");
            return;
        }
        if (!currentStep) return;
        
        updateStepMutation.mutate({
            id: currentStep.id,
            name: values.name,
            shortDescription: values.shortDescription,
            minuteStart: values.minuteStart,
            minuteEnd: values.minuteEnd,
            roomCategories: values.roomCategories,
            equipmentCategories: values.equipmentCategories,
            practitionerCategories: values.practitionerCategories,
        });
    };

    const handleEditStep = (step: any) => {
        setCurrentStep(step);
        setIsEditDialogOpen(true);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Treatment Steps</h2>
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                    <DialogTrigger asChild>
                        <Button variant='outline' className='text-primary-400 border-primary-400 hover:bg-primary-500 hover:text-white transition-all' disabled={shouldDisableAllInputs}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add Step
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                            <DialogTitle>Add Treatment Step</DialogTitle>
                            <DialogDescription>
                                Create a new step for this treatment variant. Define the timing, resources, and requirements.
                            </DialogDescription>
                        </DialogHeader>
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Step Name</FormLabel>
                                            <FormControl>
                                                <Input placeholder="e.g., Initial Assessment" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="shortDescription"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Description</FormLabel>
                                            <FormControl>
                                                <Textarea 
                                                    placeholder="Brief description of what happens during this step" 
                                                    {...field} 
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="minuteStart"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Start Time (minutes)</FormLabel>
                                                <FormControl>
                                                    <Input type="number" min="0" {...field} />
                                                </FormControl>
                                                <FormDescription>
                                                    Minutes from treatment start
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="minuteEnd"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>End Time (minutes)</FormLabel>
                                                <FormControl>
                                                    <Input type="number" min="0" {...field} />
                                                </FormControl>
                                                <FormDescription>
                                                    Minutes from treatment start
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <Tabs defaultValue="rooms">
                                    <TabsList className="grid grid-cols-3">
                                        <TabsTrigger value="rooms">Rooms</TabsTrigger>
                                        <TabsTrigger value="equipment">Equipment</TabsTrigger>
                                        <TabsTrigger value="practitioners">Practitioners</TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="rooms" className="p-4 border rounded-md">
                                        <FormField
                                            control={form.control}
                                            name="roomCategories"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Required Room Categories</FormLabel>
                                                    {roomCategoriesQuery.isLoading ? (
                                                        <div>Loading room categories...</div>
                                                    ) : roomCategoriesQuery.error ? (
                                                        <div className="text-red-500">Error loading room categories</div>
                                                    ) : (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {roomCategoriesQuery.data?.data.map((category) => {
                                                                // Ensure we're properly checking if this category is selected
                                                                // by comparing with the string representation of the ID
                                                                const categoryIdStr = category.id.toString();
                                                                const isSelected = Array.isArray(field.value) && 
                                                                    field.value.some(id => id.toString() === categoryIdStr);
                                                                
                                                                return (
                                                                    <div key={category.id} className="flex items-center space-x-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            id={`room-${category.id}`}
                                                                            value={categoryIdStr}
                                                                            checked={isSelected}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                const values = field.value || [];
                                                                                field.onChange(
                                                                                    e.target.checked
                                                                                        ? [...values, value]
                                                                                        : values.filter((v) => v !== value)
                                                                                );
                                                                            }}
                                                                        />
                                                                        <label htmlFor={`room-${category.id}`}>{category.name}</label>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </TabsContent>
                                    <TabsContent value="equipment" className="p-4 border rounded-md">
                                        <FormField
                                            control={form.control}
                                            name="equipmentCategories"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Required Equipment Categories</FormLabel>
                                                    {equipmentCategoriesQuery.isLoading ? (
                                                        <div>Loading equipment categories...</div>
                                                    ) : equipmentCategoriesQuery.error ? (
                                                        <div className="text-red-500">Error loading equipment categories</div>
                                                    ) : (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {equipmentCategoriesQuery.data?.data.map((category) => (
                                                                <div key={category.id} className="flex items-center space-x-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`equipment-${category.id}`}
                                                                        value={category.id.toString()}
                                                                        checked={field.value?.includes(category.id.toString())}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            const values = field.value || [];
                                                                            field.onChange(
                                                                                e.target.checked
                                                                                    ? [...values, value]
                                                                                    : values.filter((v) => v !== value)
                                                                            );
                                                                        }}
                                                                    />
                                                                    <label htmlFor={`equipment-${category.id}`}>{category.name}</label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </TabsContent>
                                    <TabsContent value="practitioners" className="p-4 border rounded-md">
                                        <FormField
                                            control={form.control}
                                            name="practitionerCategories"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Required Practitioner Categories</FormLabel>
                                                    {practitionerCategoriesQuery.isLoading ? (
                                                        <div>Loading practitioner categories...</div>
                                                    ) : practitionerCategoriesQuery.error ? (
                                                        <div className="text-red-500">Error loading practitioner categories</div>
                                                    ) : (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {practitionerCategoriesQuery.data?.data.map((category) => (
                                                                <div key={category.id} className="flex items-center space-x-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`practitioner-${category.id}`}
                                                                        value={category.id.toString()}
                                                                        checked={field.value?.includes(category.id.toString())}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            const values = field.value || [];
                                                                            field.onChange(
                                                                                e.target.checked
                                                                                    ? [...values, value]
                                                                                    : values.filter((v) => v !== value)
                                                                            );
                                                                        }}
                                                                    />
                                                                    <label htmlFor={`practitioner-${category.id}`}>{category.name}</label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </TabsContent>
                                </Tabs>

                                <DialogFooter>
                                    <Button type="submit" disabled={createStepMutation.isPending}>
                                        {createStepMutation.isPending ? "Creating..." : "Create Step"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </Form>
                    </DialogContent>
                </Dialog>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Treatment Timeline</CardTitle>
                    <CardDescription>
                        Manage the steps for this treatment variant. Each step has specific timing and resource requirements.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {stepsQuery.isLoading ? (
                        <div className="flex justify-center p-6">Loading steps...</div>
                    ) : stepsQuery.error ? (
                        <div className="text-red-500 p-6">Error loading steps: {stepsQuery.error.message}</div>
                    ) : stepsQuery.data?.data.length === 0 ? (
                        <div className="text-center p-6 text-muted-foreground">
                            No steps defined yet. Click "Add Step" to create your first treatment step.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Timeline</TableHead>
                                    <TableHead>Resources</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {stepsQuery.data?.data
                                    .slice()
                                    .sort((a, b) => a.minuteStart - b.minuteStart)
                                    .map((step: any) => (
                                    <TableRow key={step.id}>
                                        <TableCell>
                                            <div className="font-medium">{step.name}</div>
                                            <div className="text-sm text-muted-foreground">{step.shortDescription}</div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center">
                                                <span className="text-sm">
                                                    {step.minuteStart} - {step.minuteEnd} min
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {step.roomCategoryConstraints?.map((constraint: any) => (
                                                    <Badge key={constraint.id} variant="outline" className="bg-blue-50">
                                                        {constraint.roomCategory.name}
                                                    </Badge>
                                                ))}
                                                {step.equipmentCategoryConstraints?.map((constraint: any) => (
                                                    <Badge key={constraint.id} variant="outline" className="bg-green-50">
                                                        {constraint.equipmentCategory.name}
                                                    </Badge>
                                                ))}
                                                {step.practitionerCategoryConstraints?.map((constraint: any) => (
                                                    <Badge key={constraint.id} variant="outline" className="bg-purple-50">
                                                        {constraint.practitionerCategory.name}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => handleEditStep(step)}
                                                    disabled={shouldDisableAllInputs}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            className="text-red-500 hover:text-red-700"
                                                            disabled={shouldDisableAllInputs}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>Delete Treatment Step</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                Are you sure you want to delete this treatment step? This action cannot be undone.
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                            <AlertDialogAction 
                                                                className="bg-red-500 hover:bg-red-700"
                                                                onClick={() => {
                                                                    if(userRole !== UserRole.ADMIN) {
                                                                        toast.error("You don't have permission to do this");
                                                                        return;
                                                                    }
                                                                    deleteStepMutation.mutate({ id: step.id })
                                                                }}
                                                            >
                                                                Delete
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
                <CardFooter className="flex justify-between">
                    <div className="text-sm text-muted-foreground">
                        {stepsQuery.data?.totalCount || 0} total steps
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                        >
                            Previous
                        </Button>
                        <div className="text-sm">
                            Page {page + 1} of {stepsQuery.data?.pageCount || 1}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => p + 1)}
                            disabled={!stepsQuery.data || page >= stepsQuery.data.pageCount - 1}
                        >
                            Next
                        </Button>
                    </div>
                </CardFooter>
            </Card>

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>Edit Treatment Step</DialogTitle>
                        <DialogDescription>
                            Update the details for this treatment step.
                        </DialogDescription>
                    </DialogHeader>
                    {currentStep && (
                        <Form {...editForm}>
                            <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-6">
                                <FormField
                                    control={editForm.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Step Name</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={editForm.control}
                                    name="shortDescription"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Description</FormLabel>
                                            <FormControl>
                                                <Textarea {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={editForm.control}
                                        name="minuteStart"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Start Time (minutes)</FormLabel>
                                                <FormControl>
                                                    <Input type="number" min="0" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={editForm.control}
                                        name="minuteEnd"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>End Time (minutes)</FormLabel>
                                                <FormControl>
                                                    <Input type="number" min="0" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <Tabs defaultValue="rooms">
                                    <TabsList className="grid grid-cols-3">
                                        <TabsTrigger value="rooms">Rooms</TabsTrigger>
                                        <TabsTrigger value="equipment">Equipment</TabsTrigger>
                                        <TabsTrigger value="practitioners">Practitioners</TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="rooms" className="p-4 border rounded-md">
                                        <FormField
                                            control={editForm.control}
                                            name="roomCategories"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Required Room Categories</FormLabel>
                                                    {roomCategoriesQuery.isLoading ? (
                                                        <div>Loading room categories...</div>
                                                    ) : roomCategoriesQuery.error ? (
                                                        <div className="text-red-500">Error loading room categories</div>
                                                    ) : (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {roomCategoriesQuery.data?.data.map((category) => (
                                                                <div key={category.id} className="flex items-center space-x-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`edit-room-${category.id}`}
                                                                        value={category.id.toString()}
                                                                        checked={field.value?.includes(category.id.toString())}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            const values = field.value || [];
                                                                            field.onChange(
                                                                                e.target.checked
                                                                                    ? [...values, value]
                                                                                    : values.filter((v) => v !== value)
                                                                            );
                                                                        }}
                                                                    />
                                                                    <label htmlFor={`edit-room-${category.id}`}>{category.name}</label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </TabsContent>
                                    <TabsContent value="equipment" className="p-4 border rounded-md">
                                        <FormField
                                            control={editForm.control}
                                            name="equipmentCategories"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Required Equipment Categories</FormLabel>
                                                    {equipmentCategoriesQuery.isLoading ? (
                                                        <div>Loading equipment categories...</div>
                                                    ) : equipmentCategoriesQuery.error ? (
                                                        <div className="text-red-500">Error loading equipment categories</div>
                                                    ) : (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {equipmentCategoriesQuery.data?.data.map((category) => (
                                                                <div key={category.id} className="flex items-center space-x-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`edit-equipment-${category.id}`}
                                                                        value={category.id.toString()}
                                                                        checked={field.value?.includes(category.id.toString())}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            const values = field.value || [];
                                                                            field.onChange(
                                                                                e.target.checked
                                                                                    ? [...values, value]
                                                                                    : values.filter((v) => v !== value)
                                                                            );
                                                                        }}
                                                                    />
                                                                    <label htmlFor={`edit-equipment-${category.id}`}>{category.name}</label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </TabsContent>
                                    <TabsContent value="practitioners" className="p-4 border rounded-md">
                                        <FormField
                                            control={editForm.control}
                                            name="practitionerCategories"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Required Practitioner Categories</FormLabel>
                                                    {practitionerCategoriesQuery.isLoading ? (
                                                        <div>Loading practitioner categories...</div>
                                                    ) : practitionerCategoriesQuery.error ? (
                                                        <div className="text-red-500">Error loading practitioner categories</div>
                                                    ) : (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {practitionerCategoriesQuery.data?.data.map((category) => (
                                                                <div key={category.id} className="flex items-center space-x-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`edit-practitioner-${category.id}`}
                                                                        value={category.id.toString()}
                                                                        checked={field.value?.includes(category.id.toString())}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            const values = field.value || [];
                                                                            field.onChange(
                                                                                e.target.checked
                                                                                    ? [...values, value]
                                                                                    : values.filter((v) => v !== value)
                                                                            );
                                                                        }}
                                                                    />
                                                                    <label htmlFor={`edit-practitioner-${category.id}`}>{category.name}</label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </TabsContent>
                                </Tabs>

                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={updateStepMutation.isPending}>
                                        {updateStepMutation.isPending ? "Speichern..." : "Save Changes"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </Form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TreatmentSteps;
